import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  ArticleOutlined,
  AutoAwesomeRounded,
  BalanceOutlined,
  CheckCircleRounded,
  ChevronRightRounded,
  CloseRounded,
  CloudDoneOutlined,
  CloudOffOutlined,
  DownloadRounded,
  EditRounded,
  FactCheckOutlined,
  FolderOutlined,
  GavelRounded,
  HubOutlined,
  LockOutlined,
  RefreshRounded,
  SearchRounded,
  ShieldOutlined,
  TaskAltRounded,
  UploadFileRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatInputBar from "../components/ChatInputBar";
import {
  ACTIVE_MATTER_KEY, COPILOT_PREFILL_KEY, COPILOT_PROFILE_KEY, COPILOT_PROGRESS_KEY,
  editionConfig, matterLanguage, storageKey,
} from '../edition';

const API_BASE = process.env.REACT_APP_API_BASE || "";
const PROFILE_KEY = COPILOT_PROFILE_KEY;
const PROGRESS_KEY = COPILOT_PROGRESS_KEY;
const CASE_SESSION_KEY = storageKey('copilot.caseSessionId');

function makeId(prefix = "msg") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function getCaseSessionId() {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(CASE_SESSION_KEY);
  if (!id) {
    id = makeId("matter");
    localStorage.setItem(CASE_SESSION_KEY, id);
  }
  return id;
}

function readLocal(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return parsed && typeof parsed === "object" ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
}

const DEFAULT_PROFILE = {
  matterId: "",
  matterName: 'New matter',
  matterType: 'civil',
  clientName: "",
  jurisdiction: "",
  court: "",
  currentStage: editionConfig.defaultStage,
  charges: [],
  claims: [],
  parties: [],
  remedies: [],
  objectives: "",
  keyFacts: "",
  nextCourtDate: "",
  lawyerPreferences: "Give concise, practical answers and separate verified facts from assumptions.",
};

const SKILLS = [
  { label: "Next-best action", skill: "next-action", sample: "Based on this matter and its current stage, what are the next five actions and what is missing?", icon: <TaskAltRounded /> },
  { label: "Deadline & scope check", skill: "deadline-scope", sample: "Audit urgency, dates, forum, professional scope, authority, dependencies, and facts still requiring verification.", icon: <ShieldOutlined /> },
  { label: "Evidence audit", skill: "evidence-audit", sample: "Audit the record for missing material, contradictions, custodians, witnesses, privilege, preservation, disclosure or production needs.", icon: <FactCheckOutlined /> },
  { label: "Authority research", skill: "authority-research", sample: "Research controlling and adverse Canadian authorities for the live issue, with verified pinpoints and source links only.", icon: <SearchRounded /> },
  { label: "Resolution options", skill: "resolution", sample: "Compare realistic resolution paths, consequences, risks, missing information and client authority needed.", icon: <BalanceOutlined /> },
  { label: "Hearing preparation", skill: "hearing-preparation", sample: "Build a hearing preparation plan with proof, witness, exhibit, objection, submission and contingency workstreams.", icon: <GavelRounded /> },
  { label: "Draft work product", skill: "draft-work-product", sample: "Draft the next appropriate work product using placeholders for every unverified fact, authority, date, amount and instruction.", icon: <ArticleOutlined /> },
];

const INITIAL_MESSAGE = {
  id: "welcome",
  sender: "ai",
  time: Date.now(),
  text: {
    type: "memo",
    content: 'I’m your matter-aware Ontario and BC copilot. I use the client, matter type, province, forum, professional role and scope record before suggesting work. Attach only authorized documents; I will show whether case-law retrieval is connected before making research claims.',
    sources: [],
  },
};

export default function CaseChatCopilot({ messages, setMessages, onClose }) {
  const caseSessionId = useMemo(getCaseSessionId, []);
  const [internalMessages, setInternalMessages] = useState([INITIAL_MESSAGE]);
  const chat = messages || internalMessages;
  const setChat = setMessages || setInternalMessages;
  const [input, setInput] = useState("");
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skillMode, setSkillMode] = useState(false);
  const [file, setFile] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadConsentOpen, setUploadConsentOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [lifecycle, setLifecycle] = useState({ stages: [], disclaimer: "" });
  const [profile, setProfile] = useState(() => {
    const saved = readLocal(PROFILE_KEY, DEFAULT_PROFILE);
    return {
      ...saved,
      charges: Array.isArray(saved.charges)
        ? saved.charges
        : String(saved.charges || "").split("\n").map(item => item.trim()).filter(Boolean),
      claims: Array.isArray(saved.claims) ? saved.claims : String(saved.claims || "").split("\n").map(item => item.trim()).filter(Boolean),
      parties: Array.isArray(saved.parties) ? saved.parties : String(saved.parties || "").split("\n").map(item => item.trim()).filter(Boolean),
      remedies: Array.isArray(saved.remedies) ? saved.remedies : String(saved.remedies || "").split("\n").map(item => item.trim()).filter(Boolean),
    };
  });
  const [profileDraft, setProfileDraft] = useState(profile);
  const [completedTasks, setCompletedTasks] = useState(() => readLocal(PROGRESS_KEY, {}));
  const [profileOpen, setProfileOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const conversationStorageKey = storageKey(`copilot.conversation.${caseSessionId}`);
  const conversationRef = useRef(typeof window !== "undefined" ? localStorage.getItem(conversationStorageKey) : null);
  const chatEndRef = useRef(null);

  const currentStage = lifecycle.stages.find(stage => stage.id === profile.currentStage) || lifecycle.stages[0];
  const completedCount = Object.values(completedTasks).filter(Boolean).length;
  const totalTaskCount = lifecycle.stages.reduce((sum, stage) => sum + stage.tasks.length, 0);
  const progress = totalTaskCount ? Math.round((completedCount / totalTaskCount) * 100) : 0;

  async function refreshStatus() {
    setStatusLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/copilot/status?caseSessionId=${encodeURIComponent(caseSessionId)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setStatus(await response.json());
    } catch {
      setStatus({ aiConfigured: false, retrievalReady: false, effectiveCaseCount: 0, vectorStore: { configured: false, files: {} }, localCorpus: { uniqueCases: 0 }, session: { uploadedFiles: 0 } });
    } finally {
      setStatusLoading(false);
    }
  }

  useEffect(() => {
    refreshStatus();
    fetch(`${API_BASE}/api/copilot/lifecycle`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(setLifecycle)
      .catch(() => setSnackbar("The lifecycle blueprint could not be loaded."));

    const prefill = typeof window !== "undefined" ? localStorage.getItem(COPILOT_PREFILL_KEY) : "";
    if (prefill) {
      setInput(prefill);
      localStorage.removeItem(COPILOT_PREFILL_KEY);
    }

    const activeMatterId = profile.matterId || (typeof window !== "undefined" ? localStorage.getItem(ACTIVE_MATTER_KEY) : "");
    if (activeMatterId) {
      fetch(`${API_BASE}/api/matters/${encodeURIComponent(activeMatterId)}`)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then(({ matter }) => {
          const next = {
            ...profile,
            matterId: matter.id,
            matterName: matter.title,
            matterType: matter.matterType,
            clientName: matter.clientName,
            jurisdiction: matter.jurisdiction,
            court: matter.court,
            currentStage: matter.stage,
            charges: (matter.intake?.charges || []).map(charge => `${charge.charge || ""} ${charge.statute || ""}`.trim()).filter(Boolean),
            claims: matter.intake?.claims || matter.intake?.causesOfAction || [],
            parties: (matter.intake?.parties || []).map(party => `${party.name || ''} (${party.role || 'party'})`.trim()),
            remedies: matter.intake?.remedies || [],
            objectives: matter.objectives,
            keyFacts: (matter.facts || []).map(fact => `[${fact.status}${fact.disputed ? ", disputed" : ""}] ${fact.statement}`).join("\n"),
            nextCourtDate: matter.nextCourtDate || "",
          };
          setProfile(next);
          setProfileDraft(next);
          localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
        })
        .catch(() => setSnackbar("The active matter could not be loaded. Using the saved copilot profile."));
    }
    // The session id is stable for the lifetime of this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [chat, aiTyping]);

  function saveProfile() {
    const next = {
      ...profileDraft,
      charges: Array.isArray(profileDraft.charges)
        ? profileDraft.charges
        : String(profileDraft.charges || "").split("\n").map(item => item.trim()).filter(Boolean),
      claims: Array.isArray(profileDraft.claims) ? profileDraft.claims : String(profileDraft.claims || "").split("\n").map(item => item.trim()).filter(Boolean),
      parties: Array.isArray(profileDraft.parties) ? profileDraft.parties : String(profileDraft.parties || "").split("\n").map(item => item.trim()).filter(Boolean),
      remedies: Array.isArray(profileDraft.remedies) ? profileDraft.remedies : String(profileDraft.remedies || "").split("\n").map(item => item.trim()).filter(Boolean),
    };
    setProfile(next);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
    setProfileOpen(false);
    setSnackbar("Matter context saved. New copilot answers will use it.");
  }

  function selectStage(stageId) {
    const next = { ...profile, currentStage: stageId };
    setProfile(next);
    setProfileDraft(next);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
  }

  function toggleTask(stageId, index) {
    const key = `${stageId}:${index}`;
    const next = { ...completedTasks, [key]: !completedTasks[key] };
    setCompletedTasks(next);
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
  }

  async function uploadToSession(selectedFile) {
    const form = new FormData();
    form.append("file", selectedFile);
    form.append("caseSessionId", caseSessionId);
    form.append("origin", "copilot");
    if (profile.matterId) form.append("matterId", profile.matterId);
    const response = await fetch(`${API_BASE}/api/files/upload`, { method: "POST", body: form });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Upload failed (${response.status})`);
    return body.file;
  }

  function handleFileChange(event) {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    if (!status?.aiConfigured) {
      setSnackbar("Add OPENAI_API_KEY before uploading matter documents.");
      return;
    }
    setPendingFile(selected);
    setUploadConsentOpen(true);
  }

  async function confirmMatterUpload() {
    const selected = pendingFile;
    if (!selected) return;
    setUploadConsentOpen(false);
    setFile(selected);
    setUploading(true);
    try {
      const record = await uploadToSession(selected);
      setSnackbar(`${record.name} is attached to this matter session.`);
      await refreshStatus();
    } catch (error) {
      setFile(null);
      setSnackbar(error.message);
    } finally {
      setUploading(false);
      setPendingFile(null);
    }
  }

  async function askCopilot(question, skill) {
    const response = await fetch(`${API_BASE}/api/copilot/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        conversationId: conversationRef.current || undefined,
        message: question,
        skill: skill || "",
        caseSessionId,
        matterId: profile.matterId || undefined,
        matter: profile,
        lifecycle: { completedTasks: Object.keys(completedTasks).filter(key => completedTasks[key]) },
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Copilot request failed (${response.status})`);
    conversationRef.current = body.conversationId || body.threadId || conversationRef.current;
    if (conversationRef.current) localStorage.setItem(conversationStorageKey, conversationRef.current);
    return body;
  }

  async function handleSend() {
    const question = input.trim() || (file ? `Analyze the attached document ${file.name}.` : "");
    if (!question || aiTyping || !status?.aiConfigured) return;

    const userMessage = {
      id: makeId(),
      sender: "user",
      time: Date.now(),
      text: { type: "memo", content: question },
      fileName: file?.name,
      skill: selectedSkill?.skill || null,
    };
    setChat(current => [...current, userMessage]);
    setInput("");
    setSelectedSkill(null);
    setFile(null);
    setAiTyping(true);

    try {
      const answer = await askCopilot(question, userMessage.skill);
      setChat(current => [...current, {
        id: makeId(),
        sender: "ai",
        time: Date.now(),
        text: { type: "memo", content: answer.reply || "", sources: answer.sources || [] },
        grounding: answer.grounding,
      }]);
      await refreshStatus();
    } catch (error) {
      setChat(current => [...current, {
        id: makeId(),
        sender: "ai",
        time: Date.now(),
        error: true,
        text: { type: "memo", content: `I couldn’t complete that request. ${error.message}` },
      }]);
    } finally {
      setAiTyping(false);
    }
  }

  function exportConversation() {
    const body = chat.map(message => {
      const speaker = message.sender === "ai" ? "Lex Maple Copilot" : "User";
      return `## ${speaker}\n\n${message.text?.content || ""}`;
    }).join("\n\n---\n\n");
    const blob = new Blob([`# ${profile.matterName || "Matter"} — Copilot transcript\n\n${body}`], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "lex-maple-copilot-transcript.md";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, border: "1px solid", borderColor: "divider" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", md: "center" }}>
          <Avatar variant="rounded" sx={{ bgcolor: "primary.main", width: 44, height: 44 }}><AutoAwesomeRounded /></Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5">Personalized case copilot</Typography>
              <Chip size="small" label="Responses API" color="secondary" variant="outlined" />
              <Chip
                size="small"
                icon={status?.retrievalReady ? <CloudDoneOutlined /> : <CloudOffOutlined />}
                label={statusLoading ? "Checking knowledge…" : `${Number(status?.effectiveCaseCount || 0).toLocaleString()} verified cases`}
                color={status?.retrievalReady ? "success" : "warning"}
                variant="outlined"
                onClick={() => setKnowledgeOpen(true)}
              />
              {status?.publicCaseDiscovery?.connected && (
                <Chip
                  size="small"
                  icon={<SearchRounded />}
                  label="A2AJ public search live"
                  color="info"
                  variant="outlined"
                  component="a"
                  href="/research?view=decisions"
                  clickable
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              One matter-aware journey for intake, evidence, research, drafting, deadlines, approvals and next-step execution.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            <Tooltip title="Edit matter context"><IconButton onClick={() => { setProfileDraft(profile); setProfileOpen(true); }}><EditRounded /></IconButton></Tooltip>
            <Tooltip title="Refresh copilot status"><IconButton onClick={refreshStatus}><RefreshRounded /></IconButton></Tooltip>
            <Tooltip title="Export transcript"><IconButton onClick={exportConversation}><DownloadRounded /></IconButton></Tooltip>
            {onClose && <Tooltip title="Close"><IconButton onClick={onClose}><CloseRounded /></IconButton></Tooltip>}
          </Stack>
        </Stack>
      </Paper>

      {!statusLoading && !status?.retrievalReady && (
        <Alert
          severity="warning"
          icon={<WarningAmberRounded />}
          action={<Stack direction="row" spacing={0.5}><Button color="inherit" size="small" href="/research?view=decisions">Open case research</Button><Button color="inherit" size="small" onClick={() => setKnowledgeOpen(true)}>View setup</Button></Stack>}
          sx={{ alignItems: "center" }}
        >
          <strong>Copilot generation is not available.</strong> The searchable case library contains {Number(status?.effectiveCaseCount || 0).toLocaleString()} decisions. You can continue researching and verifying cases while the AI account is enabled.
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "230px minmax(0, 1fr) 260px", xl: "250px minmax(0, 1fr) 290px" },
          gap: 1.5,
          overflow: { xs: "auto", lg: "hidden" },
        }}
      >
        <LifecycleRail
          stages={lifecycle.stages}
          currentStageId={profile.currentStage}
          onSelect={selectStage}
          completedTasks={completedTasks}
          totalProgress={progress}
        />

        <Paper sx={{ minHeight: { xs: 680, lg: 0 }, overflow: "hidden", display: "flex", flexDirection: "column", border: "1px solid", borderColor: "divider" }}>
          <Box sx={{ px: 2, py: 1.4, borderBottom: "1px solid", borderColor: "divider", bgcolor: theme => alpha(theme.palette.secondary.main, 0.035) }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1">{currentStage?.title || "Matter workspace"}</Typography>
                <Typography variant="caption" color="text.secondary">{currentStage?.goal || "Choose a stage to focus the copilot."}</Typography>
              </Box>
              <Chip size="small" label={`${progress}% lifecycle complete`} variant="outlined" />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: { xs: 1.25, sm: 2 }, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {chat.map(message => <ChatMessage key={message.id} message={message} />)}
            {aiTyping && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 30, height: 30, bgcolor: "primary.main", fontSize: 11 }}>LM</Avatar>
                <Paper sx={{ px: 1.5, py: 1.1, border: "1px solid", borderColor: "divider" }}>
                  <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="body2">Reviewing the matter context and available sources…</Typography></Stack>
                </Paper>
              </Stack>
            )}
            <div ref={chatEndRef} />
          </Box>

          {currentStage?.prompts?.length > 0 && (
            <Box sx={{ px: 2, pt: 1, display: "flex", gap: 0.75, overflowX: "auto" }}>
              {currentStage.prompts.map(prompt => (
                <Chip key={prompt} label={prompt} onClick={() => setInput(prompt)} clickable variant="outlined" size="small" sx={{ flexShrink: 0 }} />
              ))}
            </Box>
          )}

          <ChatInputBar
            input={input}
            setInput={setInput}
            handleSend={handleSend}
            uploading={uploading}
            file={file}
            handleFileChange={handleFileChange}
            selectedSkill={selectedSkill}
            setSelectedSkill={setSelectedSkill}
            skillMode={skillMode}
            setSkillMode={setSkillMode}
            skills={SKILLS}
            onSelectSkill={skill => { setSelectedSkill(skill); setInput(skill.sample); }}
            disabled={!status?.aiConfigured}
          />
        </Paper>

        <Box sx={{ display: { xs: "grid", xl: "flex" }, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr" }, flexDirection: "column", gap: 1.5, overflowY: { lg: "auto" }, minWidth: 0 }}>
          <MatterCard profile={profile} onEdit={() => { setProfileDraft(profile); setProfileOpen(true); }} />
          <KnowledgeCard status={status} loading={statusLoading} onOpen={() => setKnowledgeOpen(true)} />
          <StageChecklist stage={currentStage} completedTasks={completedTasks} onToggle={toggleTask} />
        </Box>
      </Box>

      <ProfileDialog
        open={profileOpen}
        draft={profileDraft}
        setDraft={setProfileDraft}
        stages={lifecycle.stages}
        onClose={() => setProfileOpen(false)}
        onSave={saveProfile}
      />
      <KnowledgeDialog open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)} status={status} />
      <Dialog open={uploadConsentOpen} onClose={() => { setUploadConsentOpen(false); setPendingFile(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Authorize confidential document processing</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" icon={<LockOutlined />} sx={{ mb: 2 }}>
            {pendingFile?.name || "This document"} will be uploaded to the configured OpenAI API and attached to this copilot session.
          </Alert>
          <Typography variant="body2">Continue only if the firm is authorized to process this matter document with the configured provider and any required client consent, retention review, and contractual safeguards are in place.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => { setUploadConsentOpen(false); setPendingFile(null); }}>Cancel</Button><Button variant="contained" onClick={confirmMatterUpload}>I confirm and authorize upload</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(snackbar)} message={snackbar} autoHideDuration={4200} onClose={() => setSnackbar("")} />
    </Box>
  );
}

function LifecycleRail({ stages, currentStageId, onSelect, completedTasks, totalProgress }) {
  return (
    <Paper sx={{ minHeight: 0, overflow: "hidden", border: "1px solid", borderColor: "divider", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="overline" color="text.secondary">Case lifecycle</Typography>
        <Stack direction="row" spacing={1} alignItems="center"><LinearProgress variant="determinate" value={totalProgress} sx={{ flex: 1, height: 6, borderRadius: 6 }} /><Typography variant="caption">{totalProgress}%</Typography></Stack>
      </Box>
      <Box sx={{ p: 0.75, overflowY: "auto" }}>
        {stages.map((stage, stageIndex) => {
          const selected = stage.id === currentStageId;
          const complete = stage.tasks.length > 0 && stage.tasks.every((_, taskIndex) => completedTasks[`${stage.id}:${taskIndex}`]);
          return (
            <Button
              key={stage.id}
              fullWidth
              onClick={() => onSelect(stage.id)}
              endIcon={<ChevronRightRounded />}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                px: 1.1,
                py: 1,
                mb: 0.35,
                color: selected ? "primary.main" : "text.secondary",
                bgcolor: selected ? theme => alpha(theme.palette.secondary.main, 0.09) : "transparent",
                "& .MuiButton-endIcon": { ml: "auto" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1 }}>
                {complete ? <CheckCircleRounded color="success" sx={{ fontSize: 18 }} /> : <Box sx={{ width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", border: "1px solid", borderColor: selected ? "secondary.main" : "divider", fontSize: 10 }}>{stageIndex + 1}</Box>}
                <Typography variant="body2" sx={{ fontWeight: selected ? 750 : 600, lineHeight: 1.25 }}>{stage.shortTitle}</Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
}

function MatterCard({ profile, onEdit }) {
  const language = matterLanguage(profile);
  const liveIssues = language.type === 'civil' ? profile.claims : profile.charges;
  return (
    <Paper sx={{ p: 1.75, border: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <FolderOutlined color="primary" />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Matter memory</Typography>
        <IconButton size="small" onClick={onEdit}><EditRounded fontSize="small" /></IconButton>
      </Stack>
      <Typography variant="subtitle2">{profile.matterName || "Unnamed matter"}</Typography>
      <Typography variant="caption" color="text.secondary">{profile.clientName || "Client not set"}</Typography>
      <Divider sx={{ my: 1.25 }} />
      <InfoRow label="Jurisdiction" value={profile.jurisdiction || "Not set"} />
      <InfoRow label="Court" value={profile.court || "Not set"} />
      <InfoRow label="Next date" value={profile.nextCourtDate || "Not set"} />
      <Typography variant="caption" color="text.secondary">{language.issues}</Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        {liveIssues?.length ? liveIssues.map(item => <Chip key={item} label={item} size="small" variant="outlined" />) : <Typography variant="body2">Not set</Typography>}
      </Box>
    </Paper>
  );
}

function KnowledgeCard({ status, loading, onOpen }) {
  return (
    <Paper sx={{ p: 1.75, border: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
        <HubOutlined color={status?.retrievalReady ? "success" : "warning"} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Knowledge coverage</Typography>
      </Stack>
      {loading ? <LinearProgress /> : (
        <>
          <Typography variant="h4" sx={{ fontVariantNumeric: "tabular-nums" }}>{Number(status?.effectiveCaseCount || 0).toLocaleString()}</Typography>
          <Typography variant="caption" color="text.secondary">verified cases available to this copilot</Typography>
          <Divider sx={{ my: 1.25 }} />
          <InfoRow label="OpenAI API" value={status?.aiConfigured ? "Connected" : "Not configured"} tone={status?.aiConfigured ? "success.main" : "warning.main"} />
          <InfoRow label="Indexed files" value={String(status?.vectorStore?.files?.completed || 0)} />
          <InfoRow label="Local authorized cases" value={Number(status?.localSearchCaseCount || 0).toLocaleString()} />
          <InfoRow label="Matter files" value={String(status?.session?.uploadedFiles || 0)} />
          <Button fullWidth size="small" variant="outlined" onClick={onOpen} sx={{ mt: 0.75 }}>Coverage and setup</Button>
        </>
      )}
    </Paper>
  );
}

function StageChecklist({ stage, completedTasks, onToggle }) {
  if (!stage) return null;
  const done = stage.tasks.filter((_, index) => completedTasks[`${stage.id}:${index}`]).length;
  return (
    <Paper sx={{ p: 1.75, border: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" spacing={1} alignItems="center"><FactCheckOutlined color="primary" /><Typography variant="subtitle1" sx={{ flex: 1 }}>Stage checklist</Typography><Chip label={`${done}/${stage.tasks.length}`} size="small" /></Stack>
      <Box sx={{ mt: 1 }}>
        {stage.tasks.map((task, index) => (
          <Box key={task} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, py: 0.35 }}>
            <Checkbox size="small" checked={Boolean(completedTasks[`${stage.id}:${index}`])} onChange={() => onToggle(stage.id, index)} sx={{ p: 0.3 }} />
            <Typography variant="caption" sx={{ pt: 0.35, textDecoration: completedTasks[`${stage.id}:${index}`] ? "line-through" : "none", color: completedTasks[`${stage.id}:${index}`] ? "text.disabled" : "text.primary" }}>{task}</Typography>
          </Box>
        ))}
      </Box>
      {stage.deliverables?.length > 0 && (
        <Box sx={{ mt: 1.2, pt: 1.2, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="overline" color="text.secondary">Stage outputs</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.4 }}>
            {stage.deliverables.map(deliverable => <Chip key={deliverable} label={deliverable} size="small" variant="outlined" />)}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function InfoRow({ label, value, tone = "text.primary" }) {
  return <Stack direction="row" spacing={1} sx={{ mb: 0.7 }}><Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{label}</Typography><Typography variant="caption" sx={{ color: tone, fontWeight: 700, textAlign: "right" }}>{value}</Typography></Stack>;
}

function ChatMessage({ message }) {
  const isAI = message.sender === "ai";
  return (
    <Stack direction={isAI ? "row" : "row-reverse"} spacing={1} alignItems="flex-start">
      <Avatar sx={{ width: 30, height: 30, bgcolor: isAI ? "primary.main" : "secondary.main", fontSize: 11, fontWeight: 800 }}>{isAI ? "LM" : "YOU"}</Avatar>
      <Paper
        sx={{
          p: 1.5,
          maxWidth: "min(780px, 88%)",
          border: "1px solid",
          borderColor: message.error ? "error.main" : "divider",
          bgcolor: isAI ? "background.paper" : theme => alpha(theme.palette.secondary.main, 0.08),
          borderRadius: isAI ? "4px 16px 16px" : "16px 4px 16px 16px",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>{isAI ? "Lex Maple" : "You"}</Typography>
          {message.grounding && (
            <Chip
              size="small"
              icon={message.grounding.fileSearchUsed ? <CloudDoneOutlined /> : <CloudOffOutlined />}
              label={message.grounding.fileSearchUsed ? `Grounded in ${Number(message.grounding.effectiveCaseCount || 0).toLocaleString()} cases` : "No case retrieval used"}
              color={message.grounding.fileSearchUsed ? "success" : "default"}
              variant="outlined"
              sx={{ height: 22, fontSize: 10 }}
            />
          )}
        </Stack>
        <MsgOutput text={message.text} />
      </Paper>
    </Stack>
  );
}

export function MsgOutput({ text }) {
  const content = typeof text === "string" ? text : text?.content || "";
  return (
    <Box sx={{ "& p:last-child": { mb: 0 }, "& pre": { overflowX: "auto", p: 1.2, borderRadius: 1.5, bgcolor: "action.hover" }, "& code": { fontSize: "0.84em" } }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, children, ...props }) => {
            void node;
            const href = props.href || "";
            return /^https?:\/\//i.test(href)
              ? <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
              : <span>{children}</span>;
          },
          p: ({ node, ...props }) => {
            void node;
            return <Typography component="p" variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.65, mb: 1 }} {...props} />;
          },
        }}
      >
        {content}
      </ReactMarkdown>
      {Array.isArray(text?.sources) && text.sources.length > 0 && (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}><BalanceOutlined sx={{ fontSize: 17 }} /><Typography variant="subtitle2">Case references</Typography></Stack>
          <Stack spacing={0.6}>
            {text.sources.map((source, index) => (
              <Box key={`${source.url}-${index}`}>
                <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title ? `${source.title}${source.citation ? ` — ${source.citation}` : ""}` : source.url}</a>
                {source.quote && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{source.quote}</Typography>}
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.8 }}>Verify every authority and note it up before relying on it.</Typography>
        </Box>
      )}
    </Box>
  );
}

function ProfileDialog({ open, draft, setDraft, stages, onClose, onSave }) {
  const charges = Array.isArray(draft.charges) ? draft.charges.join("\n") : draft.charges || "";
  const claims = Array.isArray(draft.claims) ? draft.claims.join("\n") : draft.claims || "";
  const parties = Array.isArray(draft.parties) ? draft.parties.join("\n") : draft.parties || "";
  const remedies = Array.isArray(draft.remedies) ? draft.remedies.join("\n") : draft.remedies || "";
  const set = (field, value) => setDraft(current => ({ ...current, [field]: value }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Personalize the copilot to this matter</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" icon={<LockOutlined />} sx={{ mb: 2 }}>Save only the context needed for this workflow. This prototype stores the profile in this browser.</Alert>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <TextField label="Matter name" value={draft.matterName || ""} onChange={event => set("matterName", event.target.value)} />
          <TextField label="Client name or internal identifier" value={draft.clientName || ""} onChange={event => set("clientName", event.target.value)} />
          <TextField select label="Matter type" value={draft.matterType || 'civil'} onChange={event => set("matterType", event.target.value)}><MenuItem value="civil">Dispute / claim</MenuItem><MenuItem value="criminal">Criminal / regulatory</MenuItem></TextField>
          <TextField select label="Province" value={draft.jurisdiction || ""} onChange={event => set("jurisdiction", event.target.value)}><MenuItem value="">Not set</MenuItem><MenuItem value="Ontario">Ontario</MenuItem><MenuItem value="British Columbia">British Columbia</MenuItem></TextField>
          <TextField label="Court and location" value={draft.court || ""} onChange={event => set("court", event.target.value)} />
          <FormControl>
            <InputLabel id="stage-label">Current lifecycle stage</InputLabel>
            <Select labelId="stage-label" label="Current lifecycle stage" value={draft.currentStage || ""} onChange={event => set("currentStage", event.target.value)}>
              {stages.map(stage => <MenuItem key={stage.id} value={stage.id}>{stage.title}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Next court date" type="date" InputLabelProps={{ shrink: true }} value={draft.nextCourtDate || ""} onChange={event => set("nextCourtDate", event.target.value)} />
          {draft.matterType === 'criminal' ? <TextField label="Charges / allegations (one per line)" multiline minRows={4} value={charges} onChange={event => set("charges", event.target.value)} /> : <><TextField label="Parties (one per line)" multiline minRows={3} value={parties} onChange={event => set("parties", event.target.value)} /><TextField label="Claims / issues (one per line)" multiline minRows={3} value={claims} onChange={event => set("claims", event.target.value)} /><TextField label="Remedies requested (one per line)" multiline minRows={3} value={remedies} onChange={event => set("remedies", event.target.value)} /></>}
          <TextField label="Client objectives and instructions" multiline minRows={4} value={draft.objectives || ""} onChange={event => set("objectives", event.target.value)} />
          <TextField label="Verified key facts" multiline minRows={4} value={draft.keyFacts || ""} onChange={event => set("keyFacts", event.target.value)} sx={{ gridColumn: { sm: "1 / -1" } }} />
          <TextField label="How the copilot should work with you" multiline minRows={2} value={draft.lawyerPreferences || ""} onChange={event => set("lawyerPreferences", event.target.value)} sx={{ gridColumn: { sm: "1 / -1" } }} />
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={onSave}>Save matter context</Button></DialogActions>
    </Dialog>
  );
}

function KnowledgeDialog({ open, onClose, status }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copilot knowledge coverage</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">Cases available to retrieval</Typography>
            <Typography variant="h3">{Number(status?.effectiveCaseCount || 0).toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">{status?.countBasis || "No verified ingestion manifest is connected."}</Typography>
          </Paper>
          <Box>
            <Typography variant="subtitle1" gutterBottom>Connection truth</Typography>
            <InfoRow label="OpenAI key" value={status?.aiConfigured ? "Configured" : "Missing"} tone={status?.aiConfigured ? "success.main" : "warning.main"} />
            <InfoRow label="API path" value={status?.api || "Responses API"} />
            <InfoRow label="Model" value={status?.model || "Not active"} />
            <InfoRow label="Vector store" value={status?.vectorStore?.configured ? status.vectorStore.status : "Not connected"} />
            <InfoRow label="Indexed files" value={String(status?.vectorStore?.files?.completed || 0)} />
            <InfoRow label="Local authorized search" value={Number(status?.localSearchCaseCount || 0).toLocaleString()} />
            <InfoRow label="Local case records" value={String(status?.localCorpus?.uniqueCases || 0)} />
            <InfoRow
              label="Free public discovery"
              value={status?.publicCaseDiscovery?.connected ? `A2AJ · ${(status.publicCaseDiscovery.datasets || []).join(', ')}` : "Not connected"}
              tone={status?.publicCaseDiscovery?.connected ? "success.main" : "warning.main"}
            />
            <InfoRow
              label="Verified coverage"
              value={status?.coverage?.verified
                ? `${status.coverage.earliestYear || "?"}–${status.coverage.latestYear || "?"}`
                : "Not established"}
            />
          </Box>
          {status?.archiveAudit?.present && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>Supplied CanLII archive audit</Typography>
              <InfoRow label="Usable criminal decisions" value={Number(status.archiveAudit.usableCriminalDecisions || 0).toLocaleString()} />
              <InfoRow label="Duplicate records removed" value={Number(status.archiveAudit.duplicateRecords || 0).toLocaleString()} />
              <InfoRow label="CAPTCHA pages rejected" value={Number(status.archiveAudit.rejectedBlockedPages || 0).toLocaleString()} />
              <InfoRow label="Missing bundle files" value={Number(status.archiveAudit.missingBundleFiles || 0).toLocaleString()} />
              <InfoRow label="Archive coverage" value={`${status.archiveAudit.earliestYear || "?"}–${status.archiveAudit.latestYear || "?"} · ${Number(status.archiveAudit.courtCount || 0)} courts`} />
              <InfoRow
                label="Copilot access"
                value={status.archiveAudit.rightsStatus === "authorized-indexed" ? "Authorized and indexed" : "Quarantined — not used"}
                tone={status.archiveAudit.rightsStatus === "authorized-indexed" ? "success.main" : "warning.main"}
              />
            </Box>
          )}
          <Alert severity={status?.retrievalReady ? "success" : "warning"}>
            {status?.retrievalReady
              ? "Retrieval is connected. The displayed case count is tied to the active vector store and authorization manifest."
              : "The model has no verified case-law corpus to search. Generic model knowledge is not counted as case storage and must not be represented as complete CanLII coverage."}
          </Alert>
          <Box>
            <Typography variant="subtitle1" gutterBottom>Build the authorized A2AJ index</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review the A2AJ dataset card and every upstream licence, record written grants in a rights-policy file, then sync the courts you need. Records without a matching grant remain quarantined.
            </Typography>
            <Paper component="pre" variant="outlined" sx={{ p: 1.5, m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, bgcolor: "action.hover" }}>npm run corpus:a2aj -- --courts SCC,FC,FCA,ONCA --confirm-source-terms --rights-policy server/cases/rights-policy.json</Paper>
          </Box>
          <Box>
            <Typography variant="subtitle1" gutterBottom>Connect Copilot retrieval</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>Add OPENAI_API_KEY to server/.env, then upload the generated authorized directory to an OpenAI vector store.</Typography>
            <Paper component="pre" variant="outlined" sx={{ p: 1.5, m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, bgcolor: "action.hover" }}>{'npm run corpus:ingest -- --input server/corpus/a2aj/authorized --confirm-authorized --source-name "Authorized A2AJ decisions" --scope "Rights-cleared Canadian decisions"'}</Paper>
          </Box>
          <Alert severity="info" icon={<UploadFileRounded />}>CanLII’s current terms prohibit systematic downloading. This project’s ingestion command does not scrape CanLII.</Alert>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
